import { useTranslation } from "react-i18next";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { Modal } from "../../../components/modal/Modal";
import React, { useEffect, useState } from "react";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { Info } from "../../../components/Info";
import { styled } from "styled-components";
import { Checkbox } from "../../../utils/local-antd";
import { Controller, useForm } from "react-hook-form";
import { UpdateUserEntriesTableSettingsDto } from "../../../model/entries-table-settings/dto/UpdateUserEntriesTableSettingsDto";
import { entrySliceSelectors } from "../../../redux/entry/entry.selector";
import { EntriesTableMainFields } from "../../../model/entries-table-settings/types/EntriesTableMainFields.enum";
import { AtiraSelect } from "../../../components/AtiraSelect";
import { AppLangs } from "../../../model/shared/enum/AppLangs.enum";
import { UpdateUserSettingsDto } from "../../../model/user/settings/dto/UpdateUserSettingsDto";
import { TimezonePicker } from "../../../components/TimezonePicker";
import { CurrencyPicker } from "../../../components/CurrencyPicker";
import { Button } from "../../../components/Button";
import { settingsActions } from "../../../redux/settings/settings.slice";
import { entryActions } from "../../../redux/entry/entry.slice";
import i18n from "../../../i18n";
import omitBy from "lodash/omitBy";
import isNil from "lodash/isNil";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import { TestIDs } from "../../../tests/types/testIds";

const StyledCheckboxes = styled(Checkbox.Group)`
  .ant-checkbox-disabled .ant-checkbox-inner {
    background-color: ${({ theme }) => theme.gray};
    border-color: ${({ theme }) => theme.main};
  }

  .ant-checkbox-checked.ant-checkbox-disabled .ant-checkbox-inner {
    background-color: ${({ theme }) => theme.lightMain} !important;
    opacity: 0.7;
  }

  .ant-checkbox-checked.ant-checkbox-disabled .ant-checkbox-inner::after {
    border-inline-end: 2px solid ${({ theme }) => theme.white};
    border-bottom: 2px solid ${({ theme }) => theme.white};
  }
`;

const SaveButton = styled(Button)`
  margin: 0;
  &:hover {
    background-color: ${({ theme }) => theme.lightMain};
  }
`;

const DismissButton = styled(Button)`
  margin: 0;
  background-color: transparent;
  color: ${({ theme }) => theme.main};
  border: 1px solid ${({ theme }) => theme.main};

  &:hover {
    background-color: transparent;
    color: ${({ theme }) => theme.lightMain};
    border-color: ${({ theme }) => theme.lightMain};
  }
`;

export const SettingsInitialPreferencesModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const tableSettings = useAppSelector(
    entrySliceSelectors.selectUserEntryTableSettings,
  );

  const [open, setOpen] = useState(() => {
    const seenUser = user?.settings?.initialPreferencesSeen;
    const seenLocal = localStorage.getItem("atiraspace_initialPreferencesSeen");

    if (seenUser === true) {
      return false;
    }

    if (seenLocal) {
      return seenLocal !== "true";
    }

    return true;
  });

  const { watch, getValues, control, setValue } = useForm<
    Pick<UpdateUserEntriesTableSettingsDto, "mainFields"> &
      Pick<UpdateUserSettingsDto, "lang" | "timezone" | "currency">
  >({
    defaultValues: {
      mainFields: tableSettings?.mainFields || [EntriesTableMainFields.EMAIL],
      lang: i18n.language.toLowerCase() as AppLangs,
      timezone: user?.settings?.timezone || "",
      currency: user?.settings?.currency || "",
    },
  });
  const newMainFields = watch("mainFields");

  useEffect(() => {
    setValue("lang", i18n.language.toLowerCase() as AppLangs);
  }, [setValue]);

  const langOptions = Object.values(AppLangs).map((lang) => ({
    label: t(`settings.language.${lang.toLowerCase()}`),
    value: lang,
    "data-testid":
      TestIDs.general.settings.initialPreferencesModal.lang.option(lang),
  }));

  const hideModalPermanentlyLocally = () => {
    localStorage.setItem("atiraspace_initialPreferencesSeen", "true");
    setOpen(false);
  };

  const markInitialPreferencesSeenInBackground = () => {
    if (!user?._id) {
      return;
    }

    dispatch(
      settingsActions.updateSettings({
        userId: user._id,
        initialPreferencesSeen: true,
      }),
    );
  };

  const onDismiss = () => {
    hideModalPermanentlyLocally();
    markInitialPreferencesSeenInBackground();
  };

  const onSave = () => {
    const { lang, timezone, currency } = getValues();
    if (!user) {
      return;
    }

    // Don't await and don't unwrap on purpose, we want these to happen in the background and we don't want to block the UI
    dispatch(
      settingsActions.updateSettings({
        userId: user._id,
        initialPreferencesSeen: true,
        ...omitBy(
          {
            lang: lang || undefined,
            timezone: timezone || undefined,
            currency: currency || undefined,
          },
          (v) => isNil(v) || isEmpty(v),
        ),
      }),
    );
    if (
      !isEqual(
        [...(newMainFields || [])]?.sort(),
        [...(tableSettings?.mainFields || [])]?.sort(),
      )
    ) {
      dispatch(
        entryActions.updateUserTableSettings({
          mainFields: newMainFields,
          userId: user._id,
          tableId: tableSettings?._id || "",
        }),
      );
    }

    hideModalPermanentlyLocally();

    if (lang) {
      i18n.changeLanguage(lang);
    }

    setTimeout(() => {
      dispatch(settingsActions.getSettings({ userId: user!._id }));
      dispatch(entryActions.getUserTableSettings({ userId: user!._id }));
    }, 2000);
  };

  if (!open || !user || user.isE2E === true) {
    return null;
  }

  return (
    <Modal
      open
      onClose={onDismiss}
      title={t("register.welcome_message")}
      mask={{ closable: false }}
      className={TestIDs.general.settings.initialPreferencesModal.open}
    >
      <Flex flexDirection="column" gap="m">
        <Flex flexDirection="column" gap="xl">
          <Text>{t("settings.initialPreferences.description")}</Text>

          <Flex flexDirection="column" gap="xl">
            <Flex flexDirection="column" gap="m">
              <Text fontWeight="bold">{t("settings.fields.title")}</Text>
              <Info
                info={t("entries.table.settings.tab-mainFields.description")}
              />
              <Controller
                name="mainFields"
                control={control}
                render={({ field: { onChange } }) => (
                  <StyledCheckboxes
                    value={newMainFields}
                    onChange={(checkedValues) => onChange(checkedValues)}
                  >
                    <Checkbox
                      value={EntriesTableMainFields.PHONE}
                      disabled={
                        newMainFields?.includes(EntriesTableMainFields.PHONE) &&
                        newMainFields?.length === 1
                      }
                      data-testid={TestIDs.general.settings.initialPreferencesModal.mainFields.option(
                        EntriesTableMainFields.PHONE,
                      )}
                    >
                      {t("common.phone")}
                    </Checkbox>
                    <Checkbox
                      value={EntriesTableMainFields.EMAIL}
                      disabled={
                        newMainFields?.includes(EntriesTableMainFields.EMAIL) &&
                        newMainFields?.length === 1
                      }
                      data-testid={TestIDs.general.settings.initialPreferencesModal.mainFields.option(
                        EntriesTableMainFields.EMAIL,
                      )}
                    >
                      {t("common.email")}
                    </Checkbox>
                  </StyledCheckboxes>
                )}
              />
            </Flex>

            <Flex flexDirection="column" gap="s">
              <Text fontWeight="bold">{t("settings.language.title")}</Text>
              <Info info={t("settings.language.lang.description")} />
              <Controller
                name="lang"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <AtiraSelect
                    options={langOptions}
                    value={langOptions.find((option) => option.value === value)}
                    onChange={onChange}
                    valid={!error}
                    errorMessage={error?.message}
                    title={t("settings.language.lang.title")}
                    className={
                      TestIDs.general.settings.initialPreferencesModal.lang.open
                    }
                  />
                )}
              />
            </Flex>

            <Flex flexDirection="column" gap="s">
              <Text fontWeight="bold">{t("settings.timezone.title")}</Text>
              <Info info={t("input.dateConfig.timezone.info")} />
              <Controller
                name="timezone"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <TimezonePicker
                    value={value}
                    onChange={(e: any) => onChange(e?.value)}
                    error={error}
                    className={
                      TestIDs.general.settings.initialPreferencesModal.timezone
                        .open
                    }
                  />
                )}
              />
            </Flex>

            <Flex flexDirection="column" gap="s">
              <Text fontWeight="bold">{t("settings.currency.title")}</Text>
              <Info info={t("settings.general.currency.info")} />
              <Controller
                name="currency"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <CurrencyPicker
                    value={value}
                    onChange={(e: any) => onChange(e?.value)}
                    error={error}
                    className={
                      TestIDs.general.settings.initialPreferencesModal.currency
                        .open
                    }
                  />
                )}
              />
            </Flex>
          </Flex>

          <Flex flexDirection="column" gap="s">
            <SaveButton
              title={t("common.save")}
              onClick={onSave}
              dataTestId={TestIDs.general.settings.initialPreferencesModal.save}
            />
            <DismissButton
              title={t("common.dismiss")}
              onClick={onDismiss}
              dataTestId={
                TestIDs.general.settings.initialPreferencesModal.dismiss
              }
            />
          </Flex>
        </Flex>
      </Flex>
    </Modal>
  );
};
